import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { refreshQueueNow } from '../../../shared/realtime/queueRefresh'
import { createProcurementResult } from '../../../shared/api/requestItems.api'
import { ErrorMessage } from '../../../shared/ui/ErrorMessage'
import { AutoTextarea } from '../../../shared/ui/AutoTextarea'
import type { SourceType } from '../../../shared/api/types'
import { SupplierDuplicateHint } from '../../suppliers/components/SupplierDuplicateHint'

export const SOURCE_LABELS: Record<SourceType, string> = {
  STOCK: 'Склад',
  CHINA: 'Китай',
  RF: 'РФ',
  MANUFACTURER: 'Производители',
  OTHER: 'Стоки',
}

// Обратный маппинг русская метка → код SourceType. Матрица позиции
// (item.matrix) хранится как русская метка («РФ», «Склад»), а форма квоты
// оперирует кодами — конвертируем при подстановке дефолта.
const LABEL_TO_SOURCE: Record<string, SourceType> = Object.fromEntries(
  Object.entries(SOURCE_LABELS).map(([code, label]) => [label, code as SourceType]),
) as Record<string, SourceType>

/** Нормализует матрицу позиции к коду SourceType. Принимает и код ('RF'),
 *  и русскую метку ('РФ'); возвращает null, если не распознано. */
function resolveSourceType(raw: string | null | undefined): SourceType | null {
  if (!raw) return null
  if (raw in SOURCE_LABELS) return raw as SourceType
  return LABEL_TO_SOURCE[raw] ?? null
}

export function AddResultForm({
  itemId,
  onDone,
  userRole,
  defaultSourceType,
}: {
  itemId: string
  onDone: () => void
  userRole?: string
  // Матрица позиции из заявки (item.matrix). Если задана и доступна роли —
  // форма открывается сразу с этой матрицей (и валютой RUB для РФ), чтобы
  // менеджеру не приходилось выбирать матрицу вручную при добавлении квоты.
  defaultSourceType?: string | null
}) {
  const queryClient = useQueryClient()
  const isProcurement = userRole === 'PROCUREMENT'
  // Закупщику доступны только CHINA/MANUFACTURER — для него матрицу позиции
  // как дефолт не применяем (RF/STOCK/OTHER в его дропдауне нет).
  const allowedMatrices: SourceType[] = isProcurement
    ? ['CHINA', 'MANUFACTURER']
    : ['STOCK', 'CHINA', 'RF', 'MANUFACTURER', 'OTHER']
  const resolved = resolveSourceType(defaultSourceType)
  const initialSource: SourceType =
    resolved && allowedMatrices.includes(resolved) ? resolved : isProcurement ? 'CHINA' : 'STOCK'
  const [f, setF] = useState({
    supplierName: '',
    price: '',
    // РФ → рубли (другой валюты в матрице РФ не бывает), иначе CNY по умолчанию.
    currency: initialSource === 'RF' ? 'RUB' : 'CNY',
    sourceType: initialSource,
    note: '',
  })
  const [err, setErr] = useState<unknown>(null)
  // При выборе матрицы РФ валюта принудительно становится RUB (в РФ цены
  // всегда в рублях — другой валюты там не бывает). Иначе оставляем выбор
  // пользователя. При обратной смене (RF → что-то другое) возвращаем CNY
  // по умолчанию — чтобы не остался случайный RUB в валютной квоте.
  const set = (k: string, v: string) => {
    setF((prev) => {
      const next = { ...prev, [k]: v }
      if (k === 'sourceType') {
        if (v === 'RF') next.currency = 'RUB'
        else if (prev.sourceType === 'RF' && prev.currency === 'RUB') next.currency = 'CNY'
      }
      return next
    })
  }

  const mutation = useMutation({
    mutationFn: () =>
      createProcurementResult(itemId, {
        supplierName: f.supplierName,
        price: parseFloat(f.price),
        currency: f.currency,
        sourceType: f.sourceType,
        note: f.note || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['procurement-results', itemId] })
      refreshQueueNow()
      void queryClient.invalidateQueries({ queryKey: ['request-items', itemId] })
      // per-user закупщицкий state автоматически становится QUOTED при
      // создании квоты — инвалидируем, чтобы бейдж/кнопки обновились без F5.
      void queryClient.invalidateQueries({ queryKey: ['item-proc-state', itemId] })
      // И ['requests', requestId] чтобы procurementStatus заявки пересчитался.
      void queryClient.invalidateQueries({ queryKey: ['requests'] })
      // Таблица позиций в карточке заявки — иначе цена/статус не появятся
      // до F5 при возврате со страницы позиции (особенно заметно у РОП:
      // он один в сессии и создаёт квоты, и возвращается в таблицу).
      void queryClient.invalidateQueries({ queryKey: ['request-items-by-request'] })
      onDone()
    },
    onError: setErr,
  })

  return (
    <div className="bg-blue-50/50 dark:bg-[var(--bg-card)] border border-blue-100 dark:border-[var(--border)] rounded-lg p-3 space-y-2">
      <div className="text-xs font-medium text-gray-700">Новая квота</div>
      {err != null && <ErrorMessage error={err} />}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="sm:col-span-2">
          <label className="block text-xs text-gray-500 mb-0.5">Поставщик *</label>
          <input
            autoFocus
            value={f.supplierName}
            onChange={(e) => set('supplierName', e.target.value)}
            className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
            placeholder="Название компании"
          />
          <SupplierDuplicateHint name={f.supplierName} onPick={(n) => set('supplierName', n)} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Матрица</label>
          <select
            value={f.sourceType}
            onChange={(e) => set('sourceType', e.target.value)}
            className="w-full px-2 py-1 border border-gray-300 rounded text-xs bg-white focus:outline-none"
          >
            {Object.entries(SOURCE_LABELS)
              .filter(([k]) => !isProcurement || k === 'CHINA' || k === 'MANUFACTURER')
              .map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Цена за ед. *</label>
          <div className="flex gap-1">
            <input
              type="number"
              value={f.price}
              onChange={(e) => set('price', e.target.value)}
              placeholder="0.00"
              className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <select
              value={f.currency}
              onChange={(e) => set('currency', e.target.value)}
              disabled={f.sourceType === 'RF'}
              title={f.sourceType === 'RF' ? 'В матрице РФ цены всегда в рублях' : undefined}
              className="w-14 px-1 py-1 border border-gray-300 rounded text-xs bg-white focus:outline-none disabled:bg-gray-100 disabled:text-gray-500"
            >
              <option>USD</option>
              <option>EUR</option>
              <option>CNY</option>
              <option>RUB</option>
            </select>
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="flex items-center justify-between text-xs text-gray-500 mb-0.5">
            <span>Заметка</span>
            <span className="text-[10px] text-gray-400">{f.note.length}/500</span>
          </label>
          <AutoTextarea
            value={f.note}
            onChange={(e) => set('note', e.target.value.slice(0, 500))}
            maxLength={500}
            minRows={2}
            className="w-full min-h-[2.25rem] px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
            placeholder="Срок поставки, условия, ограничения поставщика…"
          />
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          onClick={() => mutation.mutate()}
          disabled={!f.supplierName || !f.price || mutation.isPending}
          className="px-3 py-1.5 bg-brand-600 text-white text-xs rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
        >
          {mutation.isPending ? '...' : 'Добавить квоту'}
        </button>
        <button onClick={onDone} className="px-3 py-1.5 text-gray-500 text-xs hover:text-gray-900">
          Отмена
        </button>
      </div>
    </div>
  )
}
